<template>
  <div class="json-viewer">
    <div v-if="isArray">
      <div v-for="(item, index) in normalizedData" :key="index">
        <!-- Recursive call for nested arrays -->
        <div v-if="isItemArray(item)" class="expanded">
          <div class="collapsible" @click.stop="toggle(index)">
            <span class="arrow" :class="{ 'rotated': isOpen(index) }">▶</span>
            <span class="key">{{ $t(item.title) }}</span>
          </div>
          <div class="children" v-show="isOpen(index)">
            <ul>
              <li v-for="(child, childIndex) in childrenOf(item)" :key="childIndex">
                <JsonViewer :data="child" />
              </li>
            </ul>
          </div>
        </div>
        
        <!-- Leaf node rendering -->
        <div v-else>
          <span class="key">{{ $t(item.title) }}:</span>
          <span class="string" :style="{ color: item.color }"> {{ item.data }}</span>
        </div>
      </div>
    </div>
    
    <!-- Single item rendering (should generally be wrapped in array by parent, but handling just in case) -->
    <div v-else-if="singleItem">
       <div v-if="isItemArray(singleItem)" class="expanded">
          <div class="collapsible" @click.stop="toggle('single')">
             <span class="arrow" :class="{ 'rotated': isOpen('single') }">▶</span>
             <span class="key">{{ $t(singleItem.title) }}</span>
          </div>
          <div class="children" v-show="isOpen('single')">
            <ul>
              <li v-for="(child, childIndex) in childrenOf(singleItem)" :key="childIndex">
                <JsonViewer :data="child" />
              </li>
            </ul>
          </div>
       </div>
       <div v-else>
          <span class="key">{{ $t(singleItem.title) }}:</span>
          <span class="string" :style="{ color: singleItem.color }"> {{ singleItem.data }}</span>
       </div>
    </div>
  </div>
</template>

<script lang="ts">
import { type PropType, computed, defineComponent, ref } from "vue";

export interface IJSONViewerData {
	title: string;
	data: string | number | boolean | null | undefined | IJSONViewerData[];
	color?: string;
}

export default defineComponent({
	name: "JsonViewer",
	props: {
		data: {
			type: [Object, Array] as PropType<IJSONViewerData | IJSONViewerData[]>,
			required: true,
		},
	},
	setup(props) {
		const openStates = ref<Record<string | number, boolean>>({});

		// Ensure data is treated as an array for the v-for, or handle single object
		const isArray = computed(() => Array.isArray(props.data));
		const normalizedData = computed(() =>
			Array.isArray(props.data) ? props.data : [props.data],
		);
		const singleItem = computed(() =>
			Array.isArray(props.data) ? null : props.data,
		);

		const isItemArray = (item: IJSONViewerData) => {
			return Array.isArray(item.data);
		};

		const childrenOf = (item: IJSONViewerData): IJSONViewerData[] =>
			Array.isArray(item.data) ? item.data : [];

		const toggle = (key: string | number) => {
			openStates.value[key] = !openStates.value[key];
		};

		const isOpen = (key: string | number) => {
			// Default to expanded? The original TS had "expanded" class by default on creation for main container
			// But let's start collapsed or expanded. PROPOSAL: Start collapsed.
			return !!openStates.value[key];
		};

		// Initialize open states to true if we want them expanded by default
		// In original: mainContainer.classList.add("expanded"); -> so yes, expanded by default
		if (isArray.value) {
			normalizedData.value.forEach((item, index) => {
				if (isItemArray(item)) {
					openStates.value[index] = true;
				}
			});
		} else {
			if (singleItem.value && isItemArray(singleItem.value)) {
				openStates.value["single"] = true;
			}
		}

		return {
			isArray,
			normalizedData,
			singleItem,
			isItemArray,
			childrenOf,
			toggle,
			isOpen,
		};
	},
});
</script>

<style scoped>
.json-viewer {
  font-family: monospace;
  font-size: 12px;
  color: #e0e0e0;
  text-align: left;
}
.json-viewer ul {
  list-style: none;
  margin: 0;
  padding: 0 0 0 15px;
}
.json-viewer li {
  margin-bottom: 2px;
}
.json-viewer .collapsible {
  cursor: pointer;
  user-select: none;
  display: inline-block;
}
.json-viewer .arrow {
  display: inline-block;
  margin-right: 4px;
  font-size: 10px;
  transition: transform 0.1s;
  color: #a0a0a0;
}
.json-viewer .arrow.rotated {
  transform: rotate(90deg);
}
.json-viewer .key {
  color: #9cdcfe;
  margin-right: 4px;
}
.json-viewer .string {
  color: #ce9178;
}
</style>
